import { useDispatch } from 'react-redux'
import { useCallback } from 'react'

import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { CompanyUserData } from 'src/State/User/UserModel'
import { updateUser } from 'src/Libs/user/api/user.api'
import { CompanyInvite } from '../components/InviteEditor'
import useUserPermissionsMutate from './use-user-permissions-mutate'
import { UserPermissionTypes } from '../types'
import { ChangeReason } from 'src/Libs/form'

interface useCompanyInstallerTableActionsHook {
  onChangeShop: (value: boolean, userId: number) => Promise<void>
  onChangeNotifications: (value: boolean, userId: number) => Promise<void>
  onSendInvite: (invite: CompanyInvite) => void
  onReSendInvite: (companyId: number, token: string) => void
  onDeleteInvitation: (token: string) => void
  onDeleteInstaller: (installer: CompanyUserData, deletionReason?: ChangeReason) => Promise<void>
  onChangeManager: (user: CompanyUserData, changeManagerReason: ChangeReason, removeOldManager: boolean, refreshTable?: () => void) => void
}

const useCompanyInstallerTableActions = (companyId: number): useCompanyInstallerTableActionsHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()
  const [enablePermissions, removePermission] = useUserPermissionsMutate({
    onSuccessDisable: () => enqueueSuccessStack(t('user.company.shopPermission.deny.success')),
    onErrorDisable: () => enqueueErrorStack(t('user.company.shopPermission.deny.error')),
    onSuccessEnable: () => enqueueSuccessStack(t('user.company.shopPermission.grant.success')),
    onErrorEnable: () => enqueueErrorStack(t('user.company.shopPermission.grant.error')),
  })

  const onChangeShop = async (value: boolean, userId: number): Promise<void> =>
    value
      ? enablePermissions.mutate({
          companyId,
          userId,
          permissionType: UserPermissionTypes.USE_SHOP,
        })
      : removePermission.mutate({
          companyId,
          userId,
          permissionType: UserPermissionTypes.USE_SHOP,
        })

  const onChangeNotifications = async (value: boolean, userId: number): Promise<void> => {
    try {
      await updateUser(
        userId,
        {
          mailNotification: value,
        },
        authFetch
      )

      enqueueSuccessStack(t('user.company.notifications.save.success'))
    } catch (error) {
      const errorData = error as AuthFetchError

      console.error(errorData)
      enqueueErrorStack(t('user.company.notifications.save.error'))
    }
  }

  const onSendInvite = (invite: CompanyInvite): void => {
    authFetch({
      path: `users/companies/${companyId}/invite`,
      method: 'POST',
      payload: {
        email: invite.email,
        app: process.env.REACT_APP_NAME,
      },
    })
      .then(() => {
        enqueueSuccessStack(t('user.company.invitation.success'))
        dispatch(refreshApiView())
      })
      .catch((error: AuthFetchError) => {
        console.error(error)
        let errorMessage = ''

        switch (error.response?.data.errno) {
          case FetchErrorCodes.UserAlreadyInCompany: {
            errorMessage = t('user.company.invitation.error.userHasCompany')
            break
          }
          case FetchErrorCodes.IncorrectUser: {
            errorMessage = t('user.company.invitation.error.userDoesNotExist')
            break
          }
          case FetchErrorCodes.DoesAlreadyExist: {
            errorMessage = t('user.company.invitation.error.userAlreadyExist')
            break
          }
          default: {
            errorMessage = t('user.company.invitation.error.general', { error: error.response?.data.error })
            break
          }
        }

        enqueueErrorStack(errorMessage)
      })
  }
  const onReSendInvite = (companyId: number, token: string): void => {
    authFetch({
      path: `users/companies/${companyId}/invite/resend/${token}`,
      method: 'POST',
    })
      .then(() => {
        enqueueSuccessStack(t('user.company.reInvitation.success'))
        dispatch(refreshApiView())
      })
      .catch((error: AuthFetchError) => {
        const errorMessage = t('user.company.reInvitation.error.general', { error: error.response?.data.error })
        enqueueErrorStack(errorMessage)
      })
  }

  const onDeleteInvitation = useCallback(
    (token: string): void => {
      authFetch({ method: 'DELETE', path: `users/companies/${companyId}/invite/decline/${token}` })
        .then(() => {
          enqueueSuccessStack(t('user.company.invitation.deletion.success'))
          dispatch(refreshApiView())
        })
        .catch((error: AuthFetchError) => enqueueErrorStack(t('user.company.invitation.deletion.error', { error: error.response?.data.error })))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId]
  )

  const onDeleteInstaller = useCallback((installer: CompanyUserData, deletionReason?: ChangeReason): Promise<void> => {
    return Promise.all([
      authFetch({
        method: 'DELETE',
        path: `users/companies/${companyId}/users/${installer.ID}`,
        payload: { reason: deletionReason?.reason },
      }),
      updateUser(installer.ID, { ipin: '' }, authFetch),
    ])
      .then(() => {
        enqueueSuccessStack(t('user.company.installerDeletion.success', { user: installer.username }))
        dispatch(refreshApiView())
      })
      .catch(() => {
        enqueueErrorStack(t('user.company.installerDeletion.Error', { user: installer.username }))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeManager = useCallback(
    (user: CompanyUserData, changeManagerReason: ChangeReason, removeOldManager: boolean, refreshTable?: () => void): void => {
      authFetch({
        method: 'PUT',
        path: `users/companies/${companyId}/manager`,
        payload: { newManagerId: user.ID, reason: changeManagerReason.reason, removeOldManager },
      })
        .then(() => {
          enqueueSuccessStack(t('user.company.companyManagerChange.success'))
          refreshTable && refreshTable()
        })
        .catch(() => enqueueErrorStack(t('user.company.companyManagerChange.Error')))
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return { onChangeShop, onChangeNotifications, onDeleteInstaller, onDeleteInvitation, onReSendInvite, onSendInvite, onChangeManager }
}

export default useCompanyInstallerTableActions
